/**
 * Opt-in tracing of the controller's reactive subscriptions, from the
 * JS/Vue side only (no OCaml involved).
 *
 * Every reactive controller RPC (`auth.profile()`, `organization.all()`,
 * `graph.database.list()`, …) resolves to a `ReleasableRef` whose `.value`
 * is written by the controller bridge each time the OCaml side re-notifies
 * the subscription. Wrapping the controller in a recursive proxy and
 * attaching a Vue watcher to every ref it hands out therefore intercepts
 * ALL controller→Vue invalidations, tagged with the dotted RPC path — the
 * JS equivalent of an LD_PRELOAD shim around the controller boundary.
 *
 * This exists to investigate subscription churn: which CRDT
 * mutations (creating a task, adding a row to a custom database of another
 * workspace, …) re-notify which auth/org/database subscriptions.
 *
 * Usage (dev tool, opt-in):
 *   1. Start the dev server with the tracer compiled in:
 *      `VITE_REACTIVITY_TRACE=1 make serve-electron …`. `App.vue` wraps the
 *      controller at creation, so even the subscriptions made during login
 *      (useProfile) are traced. Then run `make trace/reactivity` for the
 *      scripted measurement, or drive `__reactivityTrace` by hand.
 *   2. Bracket actions and read counts:
 *        __reactivityTrace.mark("create task");
 *        // … perform the action in the UI …
 *        __reactivityTrace.table({ since: "create task" });
 *      or inspect `__reactivityTrace.summary()` / `__reactivityTrace.snapshot()`.
 *
 * Caveats:
 * - Counts are Vue-visible notifications: a push carrying a value for which
 *   `Object.is(previous, next)` holds (e.g. an identical boolean) does not
 *   trigger the watcher. Object payloads are freshly decoded on every push,
 *   so those always count.
 * - Traced subscriptions are handed to the app behind a thin proxy whose
 *   `release()` stops the tracer's watcher before delegating, so explicitly
 *   released refs detach fully and stay collectable. Refs *dropped without*
 *   `release()` (and refs released through a `map`ped derivative) are still
 *   held strongly by their watcher, which disables the controller's GC-based
 *   leak warnings while tracing is enabled. Do not enable in normal use.
 */

import { isRef, watch, type Ref } from "vue";

import type { Controller } from "@ctrl/controller-rpc";

/* eslint no-console: 0 */

/**
 * Master switch for the tracer: a compile-time constant baked in by Vite
 * (never enabled in production builds, where the tracing paths are
 * statically dead and tree-shaken). Enabled by setting the env var when
 * starting the dev server, e.g.
 * `VITE_REACTIVITY_TRACE=1 make serve-electron …`.
 */
const ENABLE_TRACING: boolean = import.meta.env.VITE_REACTIVITY_TRACE === "1";

export type ReactivityTraceSubscription = {
  id: number;
  /** Dotted RPC path, e.g. "organization.get". */
  path: string;
  /** Path plus a short rendering of the call arguments. */
  label: string;
  createdAtMs: number;
  notifications: number;
  /** Set when the app called `release()` (the tracer's watcher is stopped). */
  released: boolean;
};

type TraceEvent = { atMs: number; label: string };
type TraceMark = { name: string; atMs: number };

/** Cap on retained per-notification events (grouped counts stay exact). */
const EVENT_LIMIT = 100_000;

const now = (): number => performance.now();

const renderArg = (arg: unknown): string => {
  if (typeof arg === "string") return arg;
  if (
    arg === null ||
    arg === undefined ||
    typeof arg === "number" ||
    typeof arg === "boolean" ||
    typeof arg === "bigint"
  )
    return String(arg);
  try {
    const json = JSON.stringify(arg) ?? "?";
    return json.length > 32 ? `${json.slice(0, 29)}…` : json;
  } catch {
    return "?";
  }
};

const renderLabel = (path: string, args: unknown[]): string =>
  args.length === 0 ? path : `${path}(${args.map(renderArg).join(", ")})`;

const isReleasableRef = (
  value: unknown
): value is Ref<unknown> & { release: () => unknown } =>
  isRef(value) && typeof Reflect.get(value, "release") === "function";

export class ReactivityTrace {
  /** Every reactive subscription handed out by the controller, in order. */
  readonly subscriptions: ReactivityTraceSubscription[] = [];
  /** RPC invocation counts (reactive and one-shot alike), by dotted path. */
  readonly calls: Record<string, number> = {};

  private events: TraceEvent[] = [];
  private marks: TraceMark[] = [];
  private eventsTruncated = false;
  private nextId = 1;

  recordCall(path: string): void {
    this.calls[path] = (this.calls[path] ?? 0) + 1;
  }

  /**
   * Called on every RPC result. A `ReleasableRef` result is adopted and
   * replaced by a thin proxy whose `release()` first stops the tracer's
   * watcher (so released subscriptions detach fully and can be collected),
   * then delegates. `release` must be intercepted on the way *out* because
   * the controller bridge computes it in its own proxy's `get` trap
   * (controller/vue3/ref.ml) — assigning over it would be ignored.
   */
  adoptResult(path: string, args: unknown[], value: unknown): unknown {
    if (!isReleasableRef(value)) return value;
    const { entry, stop } = this.watchSubscription(
      path,
      renderLabel(path, args),
      value
    );
    return new Proxy(value, {
      get(target, prop, receiver) {
        if (prop !== "release") return Reflect.get(target, prop, receiver);
        const release: unknown = Reflect.get(target, prop, target);
        if (typeof release !== "function") return release;
        return (...releaseArgs: unknown[]): unknown => {
          entry.released = true;
          stop();
          return Reflect.apply(release, target, releaseArgs);
        };
      },
    });
  }

  /**
   * Trace a composite ref under a fixed label — a derived `computed` built
   * on top of controller subscriptions (e.g. the `useProfile` result),
   * which the controller proxy cannot adopt because it is not an RPC
   * result. Counts its recomputations like any adopted subscription.
   */
  traceComposite<T>(label: string, ref: Ref<T> | Readonly<Ref<T>>): void {
    this.watchSubscription(label, label, ref);
  }

  /** Record a point in time to measure against with `summary`/`table`. */
  mark(name: string): void {
    this.marks.push({ name, atMs: now() });
    console.info(`[reactivity-trace] mark ${JSON.stringify(name)}`);
  }

  /**
   * Notification counts grouped by subscription label — over the whole
   * session, or since `mark(since)` (its latest occurrence).
   */
  summary(options: { since?: string } = {}): Record<string, number> {
    const from = this.sinceMs(options.since);
    if (this.eventsTruncated) {
      console.warn(
        `[reactivity-trace] event log exceeded ${EVENT_LIMIT} entries; ` +
          "mark-relative counts may be incomplete"
      );
    }
    const counts: Record<string, number> = {};
    for (const event of this.events) {
      if (event.atMs < from) continue;
      counts[event.label] = (counts[event.label] ?? 0) + 1;
    }
    return counts;
  }

  /** `console.table` of `summary(options)`, sorted by descending count. */
  table(options: { since?: string } = {}): void {
    const rows = Object.entries(this.summary(options))
      .sort(([, a], [, b]) => b - a)
      .map(([label, notifications]) => ({ label, notifications }));
    console.table(rows);
  }

  /** Full state: call counts and per-subscription totals. */
  snapshot(): {
    calls: Record<string, number>;
    subscriptions: ReactivityTraceSubscription[];
  } {
    return { calls: { ...this.calls }, subscriptions: [...this.subscriptions] };
  }

  /** Drop recorded events and marks (subscriptions stay watched). */
  reset(): void {
    this.events = [];
    this.marks = [];
    this.eventsTruncated = false;
    this.subscriptions.forEach((entry) => (entry.notifications = 0));
    Object.keys(this.calls).forEach((path) => delete this.calls[path]);
  }

  private watchSubscription<T>(
    path: string,
    label: string,
    ref: Ref<T> | Readonly<Ref<T>>
  ): { entry: ReactivityTraceSubscription; stop: () => void } {
    const entry: ReactivityTraceSubscription = {
      id: this.nextId++,
      path,
      label,
      createdAtMs: now(),
      notifications: 0,
      released: false,
    };
    this.subscriptions.push(entry);
    // Sync flush: count every push individually instead of Vue's batched
    // pre-flush, which would coalesce back-to-back notifications.
    const stop = watch(ref, () => this.notify(entry), { flush: "sync" });
    return { entry, stop };
  }

  private notify(entry: ReactivityTraceSubscription): void {
    entry.notifications += 1;
    if (this.events.length >= EVENT_LIMIT) {
      this.eventsTruncated = true;
      return;
    }
    this.events.push({ atMs: now(), label: entry.label });
  }

  private sinceMs(since: string | undefined): number {
    if (since === undefined) return -Infinity;
    for (let i = this.marks.length - 1; i >= 0; i--) {
      const mark = this.marks[i];
      if (mark.name === since) return mark.atMs;
    }
    console.warn(`[reactivity-trace] no mark named ${JSON.stringify(since)}`);
    return -Infinity;
  }
}

/**
 * Recursive proxy over the controller: namespaces are proxied lazily,
 * functions are wrapped to record the call and adopt `ReleasableRef`
 * results. Same pattern as the fake controller of the route-test harness
 * (src/__tests__/routes/harness/mount-route.ts).
 */
const wrapFunction = (
  trace: ReactivityTrace,
  fn: (...args: unknown[]) => unknown,
  path: string
): ((...args: unknown[]) => unknown) => {
  return (...args: unknown[]): unknown => {
    trace.recordCall(path);
    const result = fn(...args);
    if (result instanceof Promise) {
      return result.then((value: unknown) =>
        trace.adoptResult(path, args, value)
      );
    }
    return trace.adoptResult(path, args, result);
  };
};

const wrapNamespace = <O extends object>(
  trace: ReactivityTrace,
  obj: O,
  path: string
): O => {
  // Cache wrapped members so repeated accesses observe stable identities.
  const members = new Map<PropertyKey, unknown>();
  return new Proxy(obj, {
    get(target, prop, receiver) {
      if (typeof prop !== "string") return Reflect.get(target, prop, receiver);
      const cached = members.get(prop);
      if (cached !== undefined) return cached;
      const value: unknown = Reflect.get(target, prop, target);
      const memberPath = path === "" ? prop : `${path}.${prop}`;
      const wrapped: unknown =
        typeof value === "function"
          ? wrapFunction(trace, value.bind(target), memberPath)
          : value !== null && typeof value === "object" && !isRef(value)
            ? wrapNamespace(trace, value, memberPath)
            : value;
      if (wrapped !== value) members.set(prop, wrapped);
      return wrapped;
    },
  });
};

/**
 * Wrap `controller` so `trace` observes every call and subscription.
 * (Constrained to `object` rather than `Controller` so tests can trace
 * partial fakes; `installReactivityTrace` is the typed entry point.)
 */
export const traceController = <T extends object>(
  controller: T,
  trace: ReactivityTrace
): T => wrapNamespace(trace, controller, "");

/** The tracer installed by `installReactivityTrace`, if tracing is enabled. */
export const activeReactivityTrace = (): ReactivityTrace | undefined =>
  globalThis.__reactivityTrace;

/**
 * Wrap the controller for tracing when `ENABLE_TRACING` is set, and expose
 * the tracer as `globalThis.__reactivityTrace`; a no-op passthrough otherwise.
 */
export const installReactivityTrace = <T extends Controller>(
  controller: T
): T => {
  if (!ENABLE_TRACING) return controller;
  const trace = new ReactivityTrace();
  globalThis.__reactivityTrace = trace;
  console.info(
    "[reactivity-trace] tracing controller subscriptions — " +
      '__reactivityTrace.mark("action"), __reactivityTrace.table({ since: "action" }), ' +
      "__reactivityTrace.summary(), __reactivityTrace.snapshot()"
  );
  return traceController(controller, trace);
};
